<template>
	<div style="display: contents;">
		<!-- Trigger: variante toolbar -->
		<template v-if="buttonComponent == 'v-btn'">
			<v-tooltip top>
				<template v-slot:activator="{ on: tooltip }">
					<v-btn
						v-on="{ ...tooltip }"
						icon
						:disabled="disabled"
						:color="button.color || ''"
						@click="openDialog"
					>
						<v-icon>{{ button.icon || 'assignment' }}</v-icon>
					</v-btn>
				</template>
				<span>{{ button.tooltip || 'Genera Contratti' }}</span>
			</v-tooltip>
		</template>

		<!-- Trigger: variante dropdown menu -->
		<v-list-item
			v-else-if="buttonComponent == 'v-list-item'"
			dense
			:disabled="disabled"
			@click.stop="openDialog"
			ripple
		>
			<v-list-item-icon>
				<v-icon style="font-size: 18px">{{ button.icon || 'assignment' }}</v-icon>
			</v-list-item-icon>
			<v-list-item-content>
				<v-list-item-title>{{ button.tooltip || 'Genera Contratti' }}</v-list-item-title>
			</v-list-item-content>
		</v-list-item>

		<!-- Dialog -->
		<v-dialog v-model="dialog" max-width="900" persistent scrollable>
			<v-card class="round-card background-1">
				<v-card-title class="pa-3">
					<p class="pl-2 pl-xl-3 mb-0">{{ button.mainTitle || 'Genera Contratti' }}</p>
					<v-spacer></v-spacer>
				</v-card-title>
				<v-divider></v-divider>

				<v-card-text class="pt-4" style="min-height: 260px;">
					<!-- Spinner di caricamento: stabilizza l'altezza del dialog mentre arriva la GET info -->
					<div v-if="!loaded" class="d-flex justify-center align-center" style="min-height: 220px;">
						<v-progress-circular indeterminate color="primary"></v-progress-circular>
					</div>

					<!-- Banner riepilogo conteggi -->
					<v-alert
						v-if="loaded"
						:type="convertibleCount > 0 ? 'info' : 'warning'"
						dense
						text
						class="mb-4"
					>
						<div>
							<strong>{{ convertibleCount }}</strong>
							{{ convertibleCount === 1 ? 'riga verrà trasformata' : 'righe verranno trasformate' }} in contratto.
							<span v-if="ignored > 0">
								<strong>{{ ignored }}</strong>
								{{ ignored === 1 ? 'riga ignorata' : 'righe ignorate' }}
								(prodotti senza flag "Genera contratti").
							</span>
							<span v-if="alreadyConvertedCount > 0">
								<strong>{{ alreadyConvertedCount }}</strong>
								{{ alreadyConvertedCount === 1 ? 'riga già convertita' : 'righe già convertite' }}
								in precedenza
								({{ alreadyConvertedCount === 1 ? 'verrà saltata' : 'verranno saltate' }}).
							</span>
							<span v-if="deselectedCount > 0">
								<strong>{{ deselectedCount }}</strong>
								{{ deselectedCount === 1 ? 'riga esclusa manualmente' : 'righe escluse manualmente' }}.
							</span>
						</div>
					</v-alert>

					<!-- Campi comuni applicati a tutte le righe -->
					<div v-if="loaded && convertibleCount > 0">
						<p class="text-subtitle-2 font-weight-bold mb-2 px-3">Valori applicati a tutte le righe</p>
						<v-container fluid class="pa-0">
							<fields-generator 
								:fieldsset.sync="fieldsset"
								:model.sync="commonModel"
								ref="fieldsgenerator_common"
							></fields-generator>
						</v-container>

						<v-divider class="my-3"></v-divider>
					</div>

					<!-- Override per riga: lascia vuoto per ereditare dai valori comuni -->
					<div v-if="loaded && candidateRows.length > 0">
						<p class="text-subtitle-2 font-weight-bold mb-2 px-3">
							Override per riga
							<span class="text-caption font-weight-regular grey--text">(lascia vuoto per usare i valori comuni)</span>
						</p>
						<v-expansion-panels multiple focusable flat>
							<v-expansion-panel
								v-for="row in candidateRows"
								:key="row.id"
								:disabled="row.already_converted"
								:class="{ 'row-already-converted': row.already_converted, 'row-deselected': !row.already_converted && !rowSelected[row.id] }"
							>
								<v-expansion-panel-header>
									<div class="d-flex align-center">
										<v-checkbox
											v-if="!row.already_converted"
											v-model="rowSelected[row.id]"
											hide-details
											class="ma-0 pa-0 mr-2"
											@click.stop
										></v-checkbox>
										<v-icon v-else small color="success" class="mr-2">check_circle</v-icon>
										<div>
											<div class="font-weight-medium">{{ row.name }}</div>
											<div class="text-caption grey--text">
												{{ row.product_name }} · q.tà {{ row.quantity }}
											</div>
										</div>
										<v-spacer></v-spacer>
										<v-chip v-if="row.already_converted" x-small color="success" outlined>già convertita</v-chip>
										<v-chip v-else-if="!rowSelected[row.id]" x-small color="grey" outlined>esclusa</v-chip>
									</div>
								</v-expansion-panel-header>
								<v-expansion-panel-content>
									<v-container fluid class="pa-0 pt-3">
										<fields-generator
											v-if="rowOverrides[row.id]"
											:fieldsset.sync="fieldsset"
											:model.sync="rowOverrides[row.id]"
											:ref="'fieldsgenerator_row_' + row.id"
										></fields-generator>
									</v-container>
									<v-divider></v-divider>
								</v-expansion-panel-content>
							</v-expansion-panel>
						</v-expansion-panels>
					</div>

					<div v-if="loaded && candidateRows.length === 0" class="text-center pa-4 grey--text">
						Nessuna riga di questo abbonamento ha prodotti con flag "Genera contratti".
					</div>
				</v-card-text>

				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text color="info" @click="closeDialog">Annulla</v-btn>
					<v-btn
						text
						color="primary"
						@click="submit"
						:loading="loading"
						:disabled="convertibleCount === 0"
					>
						Genera contratti
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import FormsMixin from '@/mixins/FormsMixin'

export default {
	name: 'convert-to-contracts-dialog',
	mixins: [FormsMixin],
	props: {
		button: { type: Object, default: () => ({}) },
		params: { type: Object, default: () => ({}) },
		id: { type: [Number, String], default: null },
		model: { type: Object, default: () => ({}) },
		info: { type: Object, default: () => ({}) },
		modelDef: { type: String, default: '' },
		disabled: { type: Boolean, default: false },
		buttonComponent: { type: String, default: 'v-btn' },
	},
	data() {
		return {
			dialog: false,
			loading: false,
			loaded: false,
			fieldsset: {},
			commonModel: {},
			candidateRows: [],
			rowOverrides: {},
			rowSelected: {},
			ignored: 0,
		}
	},
	computed: {
		// Righe non già convertite e non deselezionate → entreranno nella POST.
		convertibleCount() {
			return this.candidateRows.filter(r => !r.already_converted && this.rowSelected[r.id]).length
		},
		alreadyConvertedCount() {
			return this.candidateRows.filter(r => r.already_converted).length
		},
		// Conteggio righe escluse dall'utente.
		deselectedCount() {
			return this.candidateRows.filter(r => !r.already_converted && !this.rowSelected[r.id]).length
		},
	},
	methods: {
		openDialog() {
			// Reset esplicito: se l'utente apre/chiude/riapre il dialog, evitiamo che
			// resti visibile per un istante lo stato della sessione precedente
			// (banner conteggi, bottone "Genera" abilitato) prima che fetchData risolva.
			this.loaded = false
			this.fieldsset = {}
			this.commonModel = {}
			this.candidateRows = []
			this.rowOverrides = {}
			this.rowSelected = {}
			this.ignored = 0
			this.dialog = true
			this.fetchData()
		},

		closeDialog() {
			this.dialog = false
		},

		fetchData() {
			const url = this.params.infoUrl || 'info-subscriptions-to-contracts'
			this.axios.get(url, { params: { subscription_id: this.id } }).then(response => {
				this.fieldsset = response.data.fields
				this.candidateRows = response.data.candidate_rows || []
				this.ignored = response.data.ignored || 0

				// Default comuni dai field definiti
				this.commonModel = this.buildDefaultModel()

				// Righe partono vuote: se l'utente non tocca nulla, il BE userà i valori comuni.
				// Eccezione: la SLA è precompilata con quella del prodotto della riga (modificabile)
				this.rowOverrides = {}
				this.rowSelected = {}
				this.candidateRows.forEach(row => {
					this.$set(this.rowOverrides, row.id, row.sla_id ? { sla_id: row.sla_id } : {})
					// Default: selezionata (verrà generato il contratto). L'utente
					// può deselezionare manualmente per escludere quella riga.
					this.$set(this.rowSelected, row.id, !row.already_converted)
				})

				this.loaded = true
			}).catch(() => {
				this.$store.commit('setSnackbar', {
					timeout: 4000,
					text: 'Errore caricamento dati',
					value: true,
					color: 'error',
				})
			})
		},

		buildDefaultModel() {
			const m = {}
			_.forEach(this.fieldsset.fields, (field) => {
				const def = field.default !== undefined ? field.default : (field.fieldset && field.fieldset.default !== undefined ? field.fieldset.default : undefined)
				const key = field.key || (field.fieldset && field.fieldset.key)
				if (def !== undefined && key) {
					m[key] = def
				}
			})
			return m
		},

		buildRowsPayload() {
			return this.candidateRows
				.filter(r => !r.already_converted && this.rowSelected[r.id])
				.map(row => {
					return Object.assign(
						{ product_subscription_id: row.id },
						this.rowOverrides[row.id] || {}
					)
				})
		},

		submit() {
			if (!this.id) {
				this.$store.commit('setSnackbar', {
					timeout: 4000, text: 'ID abbonamento mancante', value: true, color: 'error',
				})
				return
			}
			if (this.convertibleCount === 0) {
				this.$store.commit('setSnackbar', {
					timeout: 4000, text: 'Nessuna riga da convertire', value: true, color: 'warning',
				})
				return
			}

			this.loading = true
			const submitUrl = (this.params.submitUrl || 'subscriptions') + '/' + this.id + '/generate-contracts'
			const payload = {
				model: this.commonModel,
				rows: this.buildRowsPayload(),
				excluded_product_subscription_ids: this.candidateRows
					.filter(r => !r.already_converted && !this.rowSelected[r.id])
					.map(r => r.id),
			}

			this.axios.post(submitUrl, payload).then(response => {
				this.loading = false
				this.dialog = false
				const created = response.data?.created || 0
				const skipped = response.data?.skipped || 0
				const errors = response.data?.errors || 0
				let msg = `Generati ${created} contratti`
				if (skipped > 0) msg += `, ${skipped} saltati`
				if (errors > 0) msg += `, ${errors} errori`
				this.$store.commit('setSnackbar', {
					timeout: 6000,
					text: msg,
					value: true,
					color: errors > 0 ? 'warning' : 'success',
				})

				const createdList = response.data?.data?.created || []
				if (createdList.length === 1 && createdList[0].contract_id) {
					// Il BE indica la route corretta per il tipo di contratto generato
					// (es. helpdeskcontracts-edit); fallback sulla route statica del bottone
					const route = response.data?.contract_route || this.button.route || this.params.route || 'contracts-show'
					this.$router.push({ name: route, params: { id: createdList[0].contract_id } })
				}
			}).catch(error => {
				this.loading = false
				this.$store.commit('setSnackbar', {
					timeout: 5000,
					text: error.response?.data?.message || 'Errore generazione contratti',
					value: true,
					color: 'error',
				})
			})
		},
	},
}
</script>

<style scoped>
.row-already-converted {
	opacity: 0.55;
}
.row-deselected {
	opacity: 0.5;
}
</style>
